import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import "./index.css";
import "./i18n";
import { AuthContextProvider } from "./contexts/AuthContext";
let Sentry; // Will load lazily if required
import devConsole from "@/utils/devConsole";
import {
  setupChunkLoadRecovery,
} from "@/utils/chunkLoadRecovery";
import { shouldIgnoreSentryEvent } from "@/utils/sentryFilters";

// Import performance utilities in development
if (import.meta.env.DEV) {
  import("./utils/performanceDiagnostics.js");
  import("./utils/performanceDebugger.js").then(({ PerformanceDebugger }) => {
    window.PerformanceDebugger = PerformanceDebugger;
  });
}

setupChunkLoadRecovery();

// Track app initialization time
devConsole.time("⚡ Total App Initialization Time");
devConsole.time("🎯 React App Mount Time");

// Only initialize Sentry in production with optimized settings
if (window.location.hostname !== "localhost") {
  // Check if Sentry should be loaded (respects debug config)
  const shouldLoadSentry = () => {
    if (window.PerformanceDebugger) {
      return window.PerformanceDebugger.shouldLoad("sentry");
    }
    return true; // Default to loading if debugger not available
  };

  if (shouldLoadSentry()) {
    devConsole.time("🔍 Sentry Initialization Time");

    import("@sentry/react").then(
      ({ init, browserTracingIntegration, replayIntegration }) => {
        init({
          dsn: "https://2798b3497c17631f50a908d718f229d7@o4508841485795328.ingest.us.sentry.io/4509310920425472",
          integrations: [
            browserTracingIntegration(),
            replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ],
          // Reduced sampling rates for better performance
          tracesSampleRate: 0.1, // Only 10% of transactions
          replaysSessionSampleRate: 0.1, // Only 10% session replay
          replaysOnErrorSampleRate: 1.0, // 100% on errors is fine
          // Performance optimizations
          beforeSend(event) {
            // Don't send events during initial load
            if (window.performance && performance.timing.loadEventEnd === 0) {
              return null;
            }
            if (shouldIgnoreSentryEvent(event)) {
              return null;
            }
            return event;
          },
        });

        devConsole.timeEnd("🔍 Sentry Initialization Time");
      }
    );
  } else {
    devConsole.log(
      "⏭️ Sentry initialization skipped (disabled in performance debug)"
    );
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <AuthContextProvider>
    <App />
  </AuthContextProvider>
);

// Track when React finishes initial render
setTimeout(() => {
  devConsole.timeEnd("🎯 React App Mount Time");
  devConsole.timeEnd("⚡ Total App Initialization Time");

  // Show performance summary
  devConsole.log("\n📊 === PERFORMANCE SUMMARY ===");
  devConsole.log("1️⃣ App loads fast initially");
  devConsole.log("2️⃣ Third-party scripts load after delay");
  devConsole.log("3️⃣ Check console table for script load times");

  // Get delay from debug config
  const delay = window.PerformanceDebugger
    ? window.PerformanceDebugger.getConfig().delay
    : 2000;

  // Load third-party scripts after configured delay
  setTimeout(() => {
    if (window.loadThirdPartyScripts) {
      window.loadThirdPartyScripts();
    }
  }, delay);
}, 100); // Small delay to ensure React has rendered
